import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Radio, Mic2, Headphones, Volume2, Play, ExternalLink, Calendar, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { API } from '../App';

const DAYS_ORDER = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const DAYS_SHORT = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'];

const RadioPage = () => {
  const [content, setContent] = useState({
    title: 'STUNT RADIO',
    slogan: 'ON AIR · ON RIDE · ON FIRE',
    description: '',
    radioking_url: 'https://www.radioking.com/radio/stunt-radio',
    logo_url: '/images/stunt-radio-logo.jpg'
  });
  const [hosts, setHosts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(getCurrentDay());

  function getCurrentDay() {
    const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    return days[new Date().getDay()];
  }

  const fetchData = useCallback(async () => {
    try {
      const [contentRes, hostsRes, programsRes] = await Promise.all([
        axios.get(`${API}/radio`),
        axios.get(`${API}/radio/hosts`),
        axios.get(`${API}/radio/programs`)
      ]);
      setContent(contentRes.data);
      setHosts(hostsRes.data);
      setPrograms(programsRes.data || []);
    } catch (error) {
      console.error('Error fetching radio data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePlayClick = async () => {
    // Track listen event
    try {
      await axios.post(`${API}/radio/track-listen`);
    } catch (error) {
      console.debug('Failed to track listen:', error);
    }
    
    // Open RadioKing player
    window.open(content.radioking_url, '_blank');
  };

  // Filter programs for selected day
  const getDayPrograms = (day) => {
    return programs
      .filter(p => p.day === day || p.day === 'tous')
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/images/')) return url;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${url}`;
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FACC15]/10 via-black to-black" />
        
        <div className="container mx-auto px-4 md:px-8 max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="flex justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-[#FACC15]/20 blur-3xl rounded-full" />
                <img 
                  src={getImageUrl(content.logo_url)}
                  alt="Stunt Radio Logo"
                  className="w-72 h-72 md:w-96 md:h-96 object-contain relative z-10"
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center lg:text-left"
            >
              <h1 className="font-['Teko'] text-6xl md:text-8xl text-white mb-2">
                {content.title}
              </h1>
              <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm md:text-base mb-6">
                {content.slogan}
              </p>
              
              {content.description && (
                <p className="font-['Barlow'] text-gray-300 text-lg leading-relaxed mb-8 whitespace-pre-line">
                  {content.description}
                </p>
              )}

              {/* Play Button */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  onClick={handlePlayClick}
                  className="bg-[#FACC15] text-black font-bold uppercase tracking-widest px-8 py-6 text-lg hover:bg-[#EAB308] transition-all duration-200 group"
                  data-testid="radio-play-btn"
                >
                  <Headphones size={24} className="mr-3" />
                  Écouter en direct
                </Button>
                
                <a href={content.radioking_url} target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 px-8 py-6"
                  >
                    <ExternalLink size={20} className="mr-2" />
                    RadioKing
                  </Button>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 border-t border-white/10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center p-8 bg-[#09090b] border border-white/10"
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-[#FACC15]/10 flex items-center justify-center">
                <Radio size={32} className="text-[#FACC15]" />
              </div>
              <h3 className="font-['Teko'] text-2xl text-white mb-2">24H/24</h3>
              <p className="font-['Barlow'] text-gray-400 text-sm">
                Diffusion continue de musique et d'émissions
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center p-8 bg-[#09090b] border border-white/10"
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-[#FACC15]/10 flex items-center justify-center">
                <Mic2 size={32} className="text-[#FACC15]" />
              </div>
              <h3 className="font-['Teko'] text-2xl text-white mb-2">ÉMISSIONS LIVE</h3>
              <p className="font-['Barlow'] text-gray-400 text-sm">
                Des émissions en direct avec nos animateurs
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center p-8 bg-[#09090b] border border-white/10"
            >
              <div className="w-16 h-16 mx-auto mb-4 bg-[#FACC15]/10 flex items-center justify-center">
                <Volume2 size={32} className="text-[#FACC15]" />
              </div>
              <h3 className="font-['Teko'] text-2xl text-white mb-2">QUALITÉ HD</h3>
              <p className="font-['Barlow'] text-gray-400 text-sm">
                Son haute qualité pour une expérience optimale
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Grille des Programmes */}
      {programs.length > 0 && (
        <section className="py-24 border-t border-white/10">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
                <Calendar size={16} className="inline mr-2" />
                Programmation
              </p>
              <h2 className="font-['Teko'] text-5xl md:text-6xl text-white">
                GRILLE DES PROGRAMMES
              </h2>
            </motion.div>

            {/* Day Selector */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {DAYS_ORDER.map((day, index) => (
                <button
                  key={day}
                  onClick={() => setSelectedDay(day)}
                  className={`px-4 py-2 font-['Barlow'] uppercase text-sm tracking-wider transition-all duration-200 ${
                    selectedDay === day
                      ? 'bg-[#FACC15] text-black font-bold'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {DAYS_SHORT[index]}
                </button>
              ))}
            </div>

            {/* Programs Grid */}
            <div className="max-w-4xl mx-auto">
              <div className="bg-[#09090b] border border-white/10 overflow-hidden">
                {/* Header */}
                <div className="bg-[#FACC15]/10 px-6 py-4 border-b border-white/10">
                  <h3 className="font-['Teko'] text-2xl text-white capitalize">
                    {selectedDay}
                  </h3>
                </div>

                {/* Programs List */}
                <div className="divide-y divide-white/5">
                  {getDayPrograms(selectedDay).length === 0 ? (
                    <div className="px-6 py-12 text-center">
                      <Radio size={48} className="mx-auto text-gray-600 mb-4" />
                      <p className="font-['Barlow'] text-gray-500">
                        Pas de programme spécifique ce jour
                      </p>
                      <p className="font-['Barlow'] text-gray-600 text-sm mt-2">
                        Musique en continu 24h/24
                      </p>
                    </div>
                  ) : (
                    getDayPrograms(selectedDay).map((program) => (
                      <motion.div
                        key={program.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="px-6 py-4 flex items-start gap-4 hover:bg-white/5 transition-colors"
                      >
                        {/* Time */}
                        <div className="flex-shrink-0 w-24">
                          <div 
                            className="font-['Teko'] text-2xl"
                            style={{ color: program.color || '#FACC15' }}
                          >
                            {program.start_time}
                          </div>
                          <div className="font-['Barlow'] text-gray-500 text-xs">
                            → {program.end_time}
                          </div>
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                          <h4 className="font-['Teko'] text-xl text-white uppercase">
                            {program.title}
                          </h4>
                          {program.host_name && (
                            <p className="font-['Barlow'] text-[#FACC15] text-sm">
                              <Mic2 size={12} className="inline mr-1" />
                              {program.host_name}
                            </p>
                          )}
                          {program.description && (
                            <p className="font-['Barlow'] text-gray-400 text-sm mt-1">
                              {program.description}
                            </p>
                          )}
                        </div>

                        {/* Live indicator for current program */}
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0 mt-2"
                          style={{ backgroundColor: program.color || '#FACC15' }}
                        />
                      </motion.div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Animateurs */}
      {hosts.length > 0 && (
        <section className="py-24 bg-[#09090b] border-t border-white/10">
          <div className="container mx-auto px-4 md:px-8 max-w-7xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <p className="font-['Barlow'] text-[#FACC15] uppercase tracking-[0.3em] text-sm mb-4">
                L'équipe
              </p>
              <h2 className="font-['Teko'] text-5xl md:text-6xl text-white">
                NOS ANIMATEURS
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {hosts.map((host, index) => (
                <motion.div
                  key={host.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                >
                  <div className="relative overflow-hidden bg-black border border-white/10 hover:border-[#FACC15]/50 transition-colors duration-300">
                    {/* Photo */}
                    <div className="aspect-square bg-[#FACC15]/10 flex items-center justify-center overflow-hidden">
                      {host.photo_url ? (
                        <img 
                          src={getImageUrl(host.photo_url)}
                          alt={host.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <span className="font-['Teko'] text-6xl text-[#FACC15]">
                          {host.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      )}
                    </div>
                    
                    {/* Info */}
                    <div className="p-6">
                      <h3 className="font-['Teko'] text-2xl text-white group-hover:text-[#FACC15] transition-colors">
                        {host.name}
                      </h3>
                      <p className="font-['Barlow'] text-[#FACC15] text-sm uppercase tracking-wider mb-2">
                        {host.role}
                      </p>
                      {host.bio && (
                        <p className="font-['Barlow'] text-gray-400 text-sm line-clamp-3">
                          {host.bio}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 border-t border-white/10">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Teko'] text-4xl md:text-5xl text-white mb-6">
              REJOIGNEZ-NOUS SUR LES ONDES
            </h2>
            <p className="font-['Barlow'] text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Écoutez STUNT RADIO où que vous soyez - sur votre ordinateur, smartphone ou tablette.
            </p>
            <Button
              onClick={handlePlayClick}
              className="bg-[#FACC15] text-black font-bold uppercase tracking-widest px-12 py-6 text-lg hover:bg-[#EAB308] transition-all duration-200"
            >
              <Play size={24} className="mr-3" />
              Lancer la radio
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RadioPage;
